"use client";

import type React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  Button,
  HStack,
  VStack,
  Flex,
  IconButton,
  useToast,
  Spinner,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { ExternalLinkIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { Trash2, Plus } from "lucide-react";

import {
  agentService,
  type RetellLLM,
} from "../../../voice-services/agentService";
import {
  knowledgeBaseService,
  type KnowledgeBase,
} from "../../../voice-services/knowledgeBaseService";

interface AgentKnowledgeBaseProps {
  agentId: string;
  llmId: string;
  initialLLM: RetellLLM | null;
  onLLMUpdate: (updatedLLM: RetellLLM) => void;
}

/* -------------------------------------------
   1) Re-usable CustomSelectWithIcons (no theme checks)
-------------------------------------------- */

interface Option {
  value: string; // unique identifier
  label: string; // text to display
  icon: React.ReactElement; // icon to display
}

interface CustomSelectWithIconsProps {
  label?: string;
  options: Option[];
  onSelect: (value: string) => void;
  isDisabled?: boolean;
  buttonText?: string;
  buttonLeftIcon?: React.ReactElement;
  menuWidthMatchButton?: boolean; // whether to match MenuList width to the button
}

const CustomSelectWithIcons: React.FC<CustomSelectWithIconsProps> = ({
  label,
  options,
  onSelect,
  isDisabled,
  buttonText,
  buttonLeftIcon,
  menuWidthMatchButton = true,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Box>
      {label && (
        <Text mb={2} fontWeight="medium" color="gray.800">
          {label}
        </Text>
      )}
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          leftIcon={buttonLeftIcon}
          rightIcon={<ChevronDownIcon />}
          size="xs"
          variant="outline"
          colorScheme="primary"
          isDisabled={isDisabled}
          bg="transparent"
          color="gray.800"
          borderColor="primary.300"
          _hover={{ bg: "primary.50", borderColor: "primary.400" }}
          _active={{ bg: "primary.100", borderColor: "primary.500" }}
          _expanded={{ bg: "white", borderColor: "primary.500" }}
          _focusVisible={{
            borderColor: "primary.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
          }}
          width="100%"
        >
          {buttonText || "Select"}
        </MenuButton>
        <MenuList
          bg="gray.100"
          borderColor="primary.200"
          minW={
            menuWidthMatchButton && buttonRef.current
              ? `${buttonRef.current.offsetWidth}px`
              : "100%"
          }
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "var(--chakra-colors-primary-400) transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "var(--chakra-colors-primary-400)",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "var(--chakra-colors-primary-500)",
            },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              icon={option.icon}
              bg="gray.100"
              color="gray.800"
              fontSize="sm"
              _hover={{ bg: "primary.50" }}
              _focus={{ bg: "primary.50" }}
              _active={{ bg: "primary.100" }}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

/* -------------------------------------------
   2) AgentKnowledgeBase (aligned with Dashboard theme)
-------------------------------------------- */

export const AgentKnowledgeBase: React.FC<AgentKnowledgeBaseProps> = ({
  agentId,
  llmId,
  initialLLM,
  onLLMUpdate,
}) => {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState<
    KnowledgeBase[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();

  // Load all knowledge bases and the ones connected to this agent
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Get all knowledge bases
        const allKnowledgeBases = await knowledgeBaseService.list();
        setKnowledgeBases(allKnowledgeBases);

        // Get the knowledge bases connected to this agent
        if (
          initialLLM?.knowledge_base_ids &&
          initialLLM.knowledge_base_ids.length > 0
        ) {
          const connectedKnowledgeBases = allKnowledgeBases.filter((kb) =>
            initialLLM.knowledge_base_ids?.includes(kb.knowledge_base_id),
          );
          setSelectedKnowledgeBases(connectedKnowledgeBases);
        }
      } catch (error) {
        console.error("Error loading knowledge bases:", error);
        toast({
          title: "Error loading knowledge bases",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [initialLLM, toast]);

  // Update the LLM with the selected knowledge bases
  const updateKnowledgeBases = useCallback(
    async (kbIds: string[] | null) => {
      if (!llmId) return;

      setIsUpdating(true);
      try {
        const updatedLLM = await agentService.updateRetellLLM(llmId, {
          knowledge_base_ids: kbIds,
        });

        onLLMUpdate(updatedLLM);

        toast({
          title: "Knowledge bases updated",
          status: "success",
          duration: 2000,
        });

        return updatedLLM;
      } catch (error: any) {
        console.error("Error updating knowledge bases:", error);
        toast({
          title: "Error updating knowledge bases",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [llmId, onLLMUpdate, toast],
  );

  // Add a knowledge base to the agent
  const handleAddKnowledgeBase = useCallback(
    async (knowledgeBaseId: string) => {
      if (knowledgeBaseId === "add_new_kb") {
        localStorage.setItem("currentAgentTab", "knowledge-base");
        window.open("/voice-agents", "_blank");
        return;
      }

      const knowledgeBase = knowledgeBases.find(
        (kb) => kb.knowledge_base_id === knowledgeBaseId,
      );
      if (!knowledgeBase) return;

      if (
        selectedKnowledgeBases.some(
          (skb) => skb.knowledge_base_id === knowledgeBaseId,
        )
      ) {
        toast({
          title: "Knowledge base already added",
          status: "info",
          duration: 2000,
        });
        return;
      }

      const newSelectedKnowledgeBases = [
        ...selectedKnowledgeBases,
        knowledgeBase,
      ];
      setSelectedKnowledgeBases(newSelectedKnowledgeBases);

      const kbIds = newSelectedKnowledgeBases.map((kb) => kb.knowledge_base_id);
      await updateKnowledgeBases(kbIds);
    },
    [knowledgeBases, selectedKnowledgeBases, updateKnowledgeBases, toast],
  );

  // Remove a knowledge base from the agent
  const handleRemoveKnowledgeBase = useCallback(
    async (knowledgeBaseId: string) => {
      const newSelectedKnowledgeBases = selectedKnowledgeBases.filter(
        (kb) => kb.knowledge_base_id !== knowledgeBaseId,
      );
      setSelectedKnowledgeBases(newSelectedKnowledgeBases);

      const kbIds =
        newSelectedKnowledgeBases.length > 0
          ? newSelectedKnowledgeBases.map((kb) => kb.knowledge_base_id)
          : null;
      await updateKnowledgeBases(kbIds);
    },
    [selectedKnowledgeBases, updateKnowledgeBases],
  );

  // Build dropdown options
  const availableKnowledgeBases = knowledgeBases.filter(
    (kb) =>
      !selectedKnowledgeBases.some(
        (skb) => skb.knowledge_base_id === kb.knowledge_base_id,
      ),
  );

  const kbOptions: Option[] = availableKnowledgeBases.map((kb) => ({
    value: kb.knowledge_base_id,
    label: kb.knowledge_base_name,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M3.25 14.875V4.75C3.25 4.15326 3.48705 3.58097 3.90901 3.15901C4.33097 2.73705 4.90326 2.5 5.5 2.5H16C16.1989 2.5 16.3897 2.57902 16.5303 2.71967C16.671 2.86032 16.75 3.05109 16.75 3.25V16.75C16.75 16.9489 16.671 17.1397 16.5303 17.2803C16.3897 17.421 16.1989 17.5 16 17.5H5.875C5.17881 17.5 4.51113 17.2234 4.01884 16.7312C3.52656 16.2389 3.25 15.5712 3.25 14.875ZM15.25 16V13.75H5.875C5.57663 13.75 5.29048 13.8685 5.0795 14.0795C4.86853 14.2905 4.75 14.5766 4.75 14.875C4.75 15.1734 4.86853 15.4595 5.0795 15.6705C5.29048 15.8815 5.57663 16 5.875 16H15.25ZM4.75 12.5028C5.10152 12.3358 5.48586 12.2495 5.875 12.25H15.25V4H5.5C5.30109 4 5.11032 4.07902 4.96967 4.21967C4.82902 4.36032 4.75 4.55109 4.75 4.75V12.5028Z"
          fill="currentColor"
        />
      </svg>
    ),
  }));

  const specialOption: Option = {
    value: "add_new_kb",
    label: "Add New Knowledge Base",
    icon: <ExternalLinkIcon />,
  };

  const dropdownOptions = [...kbOptions, specialOption];

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            bg="transparent"
            _hover={{ bg: "primary.50" }}
            _active={{ bg: "primary.100" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
              color="primary.600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M3.25 14.875V4.75C3.25 4.15326 3.48705 3.58097 3.90901 3.15901C4.33097 2.73705 4.90326 2.5 5.5 2.5H16C16.1989 2.5 16.3897 2.57902 16.5303 2.71967C16.671 2.86032 16.75 3.05109 16.75 3.25V16.75C16.75 16.9489 16.671 17.1397 16.5303 17.2803C16.3897 17.421 16.1989 17.5 16 17.5H5.875C5.17881 17.5 4.51113 17.2234 4.01884 16.7312C3.52656 16.2389 3.25 15.5712 3.25 14.875ZM15.25 16V13.75H5.875C5.57663 13.75 5.29048 13.8685 5.0795 14.0795C4.86853 14.2905 4.75 14.5766 4.75 14.875C4.75 15.1734 4.86853 15.4595 5.0795 15.6705C5.29048 15.8815 5.57663 16 5.875 16H15.25ZM4.75 12.5028C5.10152 12.3358 5.48586 12.2495 5.875 12.25H15.25V4H5.5C5.30109 4 5.11032 4.07902 4.96967 4.21967C4.82902 4.36032 4.75 4.55109 4.75 4.75V12.5028Z"
                  fill="currentColor"
                />
              </svg>
              Knowledge Base
            </Box>
            <AccordionIcon color="primary.600" />
          </AccordionButton>
        </h2>

        <AccordionPanel pb={4} bg="primary.50">
          {isLoading ? (
            <Box textAlign="center" py={4}>
              <Spinner size="sm" color="primary.500" />
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Add knowledge base to provide context to the agent.
              </Text>

              {/* Selected knowledge bases */}
              {selectedKnowledgeBases.length > 0 && (
                <VStack spacing={2} align="stretch">
                  {selectedKnowledgeBases.map((kb) => (
                    <Flex
                      key={kb.knowledge_base_id}
                      justify="space-between"
                      align="center"
                      p={1.5}
                      bg="gray.50"
                      borderRadius="md"
                      minHeight="32px"
                    >
                      <HStack>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M3.25 14.875V4.75C3.25 4.15326 3.48705 3.58097 3.90901 3.15901C4.33097 2.73705 4.90326 2.5 5.5 2.5H16C16.1989 2.5 16.3897 2.57902 16.5303 2.71967C16.671 2.86032 16.75 3.05109 16.75 3.25V16.75C16.75 16.9489 16.671 17.1397 16.5303 17.2803C16.3897 17.421 16.1989 17.5 16 17.5H5.875C5.17881 17.5 4.51113 17.2234 4.01884 16.7312C3.52656 16.2389 3.25 15.5712 3.25 14.875ZM15.25 16V13.75H5.875C5.57663 13.75 5.29048 13.8685 5.0795 14.0795C4.86853 14.2905 4.75 14.5766 4.75 14.875C4.75 15.1734 4.86853 15.4595 5.0795 15.6705C5.29048 15.8815 5.57663 16 5.875 16H15.25ZM4.75 12.5028C5.10152 12.3358 5.48586 12.2495 5.875 12.25H15.25V4H5.5C5.30109 4 5.11032 4.07902 4.96967 4.21967C4.82902 4.36032 4.75 4.55109 4.75 4.75V12.5028Z"
                            fill="currentColor"
                          />
                        </svg>
                        <Text fontSize="xs" color="gray.800">
                          {kb.knowledge_base_name}
                        </Text>
                      </HStack>
                      <Tooltip label="Remove">
                        <IconButton
                          aria-label="Remove"
                          icon={<Trash2 size={12} />}
                          size="xs"
                          variant="ghost"
                          isDisabled={isUpdating}
                          onClick={() =>
                            handleRemoveKnowledgeBase(kb.knowledge_base_id)
                          }
                          color="gray.700"
                          _hover={{ bg: "primary.50", color: "primary.700" }}
                          _active={{ bg: "primary.100" }}
                          _focusVisible={{
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-primary-500)",
                          }}
                        />
                      </Tooltip>
                    </Flex>
                  ))}
                </VStack>
              )}

              {/* Add button via CustomSelectWithIcons */}
              <CustomSelectWithIcons
                label=""
                options={dropdownOptions}
                onSelect={handleAddKnowledgeBase}
                isDisabled={isUpdating}
                buttonText="Add"
                buttonLeftIcon={<Plus size={14} />}
                menuWidthMatchButton
              />
            </VStack>
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
